/**
 * mobile-inbox-header.tsx — Đầu trang Hộp thư trên ĐIỆN THOẠI, bám mẫu Messenger của Lark Mobile:
 *   ① ảnh + tên người dùng + tên tổ chức (chữ hoa nhỏ) — bấm mở ngăn "Cá nhân" xổ ngang từ trái (hồ sơ, gói, cài đặt… — mobile-account-drawer.tsx;
 *      chấm đỏ khi còn việc ở trang Bắt đầu) · nút tìm kiếm · nút "+" (menu tạo / thao tác)
 *   ② hàng avatar tròn có nhãn = tài khoản kênh, chọn = lọc theo tài khoản. CỐ ĐỊNH 6 ô, không cuộn ngang: "Tất cả" + 4 kênh đầu (theo thứ tự
 *      người dùng tự xếp) + nút mở rộng ở ô thứ 6 → hộp "Tài khoản kênh" (mobile-channels-sheet.tsx): danh sách đầy đủ để chọn;
 *      bấm "Sắp xếp" trong hộp mới hiện chế độ kéo-thả đổi thứ tự (lưu theo người dùng, đồng bộ thiết bị — hooks/use-mobile-channels.ts; logic thuần lib/mobile-channels.ts)
 *   ③ nút lọc + thanh phân đoạn (mặc định "Trò chuyện · Chưa đọc N · Nhóm N"; người dùng chọn / sắp xếp / ẩn bằng nút "Sửa" ở cuối thanh —
 *      mobile-segments.ts); nút lọc mở NGĂN KÉO trượt ngang từ cạnh trái (hoặc vuốt từ mép trái màn hình — hooks/use-edge-swipe.ts)
 *      (kiểu Lark): chính là cột bộ lọc của máy tính (FilterRail, biến thể 'drawer') — cùng chức năng: bộ lọc + số đếm, nhãn,
 *      chế độ xem tuỳ chỉnh, tuỳ chỉnh bộ lọc — kèm Ưu tiên / Nội bộ và nhóm kênh ở đầu ngăn kéo.
 * Thay hoàn toàn khối tiêu đề + tìm kiếm + chip của danh sách ở máy tính (ConversationList chỉ nhận làm khe `mobileHeader`).
 *
 * Cuộn danh sách xuống: ① và ② tự thu gọn (component `Collapsible` bên dưới) để dành chỗ xem hội thoại, ③ (nút lọc +
 * phân đoạn) luôn hiện — ConversationList đo vị trí cuộn thật (use-scroll-collapse.ts) rồi truyền xuống qua `collapsed`.
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCheck, ChevronDown, MessageSquarePlus, Plus, RefreshCw, Search, SlidersHorizontal, Tag as TagIcon, UserPlus, X } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { SideDrawer, SideDrawerContent } from '@/components/ui/side-drawer'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { PlatformBadge } from '@/components/shared/platform-mark'
import { ChannelChips, type ChannelFilterValue } from '@/components/shared/channel-chips'
import { FILTER_LABEL, type ConvFilter } from './filter-rail'
import { isSegmentActive, segmentCountOf, segmentLabel, segmentTarget, tagOfSegment, type SegmentId } from './mobile-segments'
import { useMobileSegments } from '@/hooks/use-mobile-segments'
import { useMobileChannels } from '@/hooks/use-mobile-channels'
import { hasExpand, orderAccounts, visibleAccounts } from '@/lib/mobile-channels'
import { otherAccountUnread, shortUnreadCount } from '@/lib/inbox-account'
import { SegmentsSheet } from './mobile-segments-sheet'
import { ChannelsSheet } from './mobile-channels-sheet'
import { MobileAccountDrawer } from '@/components/shared/mobile-account-drawer'
import { useOnboardingEntry } from '@/components/shared/onboarding-entry'
import { useEdgeSwipe } from '@/hooks/use-edge-swipe'
import { useTags } from '@/hooks/use-settings'
import type { ChannelGroup } from '@/lib/channel-groups'
import type { ConversationCounts, ConvTab } from '@/hooks/use-conversations'
import type { ChannelAccount } from '@/hooks/use-integrations'
import { isSandboxAccount } from '@/lib/account-status'
import { platformLabelOf } from '@/lib/platform-labels'
import { avatarSrc } from '@/pages/settings/settings-utils'
import { cn, initials } from '@/lib/utils'

/** Số hiển thị cạnh phân đoạn (rút gọn 999+); 0 / không có → không hiện. */
export const shortCount = (n: number) => (n > 999 ? '999+' : String(n))

/** Tài khoản kênh hiện ở hàng avatar: bỏ kênh nội bộ (chat thử AI). */
export const stripAccounts = (accounts: ChannelAccount[] | undefined) => (accounts ?? []).filter((a) => !isSandboxAccount(a))

export interface MobileInboxHeaderProps {
  /** Danh sách đã cuộn xuống khỏi đầu — thu gọn phần tên + hàng tài khoản kênh, ghim nguyên phần bộ lọc (use-scroll-collapse.ts). */
  collapsed?: boolean
  user: { id?: string; impersonated?: boolean; fullName: string; avatarUrl?: string | null; orgName?: string | null }
  search: string
  onSearchChange: (v: string) => void
  accounts: ChannelAccount[]
  accountId?: string
  onAccountChange: (id?: string) => void
  filter: ConvFilter
  onFilterChange: (f: ConvFilter) => void
  /** Nhãn khách đang lọc (phân đoạn nhãn / nhãn chọn trong ngăn kéo). */
  tag?: string
  onTagChange: (tag?: string) => void
  /** Đang mở chế độ xem tuỳ chỉnh (?sv=) → không tô phân đoạn nào. */
  viewActive?: boolean
  counts?: ConversationCounts
  tab: ConvTab
  onTabChange: (t: ConvTab) => void
  tabUnread: Record<ConvTab, number>
  channelGroups: ChannelGroup[]
  channel: ChannelFilterValue
  onChannelChange: (v: ChannelFilterValue) => void
  onNewConversation: () => void
  onAddFriend: () => void
  onMarkAllRead: () => void
  onRefresh: () => void
  /** Dựng cột bộ lọc của máy tính cho ngăn kéo (trang Hộp thư giữ toàn bộ trạng thái lọc nên dựng ở đó). */
  renderFilterRail: (o: { onNavigate: () => void; topSlot: React.ReactNode }) => React.ReactNode
}

export function MobileInboxHeader(p: MobileInboxHeaderProps) {
  const { t } = useTranslation('convList')
  const [searchOpen, setSearchOpen] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [accountsOpen, setAccountsOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const onboardingPending = useOnboardingEntry().show
  const avatar = avatarSrc(p.user.avatarUrl)

  // Phân đoạn tuỳ chỉnh: lưu theo người dùng, đồng bộ giữa các thiết bị (hooks/use-mobile-segments.ts). Nhãn đã xoá thì tự bỏ.
  const { data: tagDefs } = useTags()
  const tagNames = useMemo(() => tagDefs?.map((tg) => tg.name), [tagDefs])
  const { segments, setSegments: changeSegments } = useMobileSegments(p.user.id, { tagNames, readOnly: p.user.impersonated })

  // Thứ tự tài khoản kênh do người dùng xếp (lưu theo người dùng, đồng bộ thiết bị). Kênh mới kết nối chưa có trong thứ tự → đứng cuối.
  const { order: channelOrder, setOrder: setChannelOrder } = useMobileChannels(p.user.id, { readOnly: p.user.impersonated })
  const orderedAccounts = useMemo(() => orderAccounts(p.accounts, channelOrder), [p.accounts, channelOrder])
  const shownAccounts = useMemo(() => visibleAccounts(orderedAccounts, p.accountId), [orderedAccounts, p.accountId])
  const channelsDefault = useMemo(() => orderedAccounts.every((a, i) => a.id === p.accounts[i]?.id), [orderedAccounts, p.accounts])
  // Đang xem MỘT tài khoản: các tài khoản khác có tin chưa đọc thì hiện huy hiệu (lib/inbox-account.ts); kênh bị giấu sau "Mở rộng" gộp vào nút đó.
  const unreadOf = (id: string) => otherAccountUnread(p.counts?.unreadByAccount, p.accountId, id)
  const hiddenUnread = orderedAccounts.filter((a) => !shownAccounts.some((s) => s.id === a.id)).reduce((n, a) => n + unreadOf(a.id), 0)

  const state = { filter: p.filter, tag: p.tag, viewActive: p.viewActive }
  const segmentActive = (id: SegmentId) => isSegmentActive(id, state)
  function selectSegment(id: SegmentId) {
    const target = segmentTarget(id)
    p.onFilterChange(target.filter) // đổi bộ lọc = rời chế độ xem tuỳ chỉnh (trang xử lý)
    p.onTagChange(target.tag)
  }
  // Bộ lọc / phân loại đang áp dụng mà thanh phân đoạn không thể hiện → chấm xanh trên nút lọc + chip "Đang lọc".
  const noneActive = !segments.some(segmentActive)
  const hiddenFilter = noneActive || p.tab !== 'main' || p.channel !== 'all'
  const hiddenLabel = !noneActive || p.viewActive ? undefined : p.tag ? t('rail.tagLabel', { tag: p.tag }) : p.filter !== 'all' ? FILTER_LABEL[p.filter] : undefined
  // Vuốt từ mép trái màn hình → mở ngăn kéo bộ lọc (tắt khi đang có hộp nào mở).
  useEdgeSwipe(() => setFilterOpen(true), !filterOpen && !accountsOpen && !editOpen && !accountOpen)

  // Đang cuộn danh sách: thu gọn ① (tên) và ② (hàng tài khoản kênh) để dành chỗ; ③ (bộ lọc) luôn hiện — xem component Collapsible
  // bên dưới. Ô tìm kiếm đang mở thì GIỮ NGUYÊN dù đang cuộn — đóng ô tìm kiếm giữa chừng lúc đang gõ/xem kết quả sẽ khó chịu.
  const collapsed = !!p.collapsed
  const hideTopRow = collapsed && !searchOpen

  return (
    <div className="px-4 pt-[calc(0.75rem+env(safe-area-inset-top))]">
      {/* ① Người dùng + tìm kiếm + "+" */}
      <Collapsible collapsed={hideTopRow}>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setAccountOpen(true)}
            aria-label={t('mhdr.accountAria')}
            aria-haspopup="dialog"
            className="flex min-w-0 flex-1 items-center gap-3 rounded-xl text-left active:opacity-70"
          >
            <span className="relative shrink-0">
              <Avatar className="h-11 w-11">
                {avatar && <AvatarImage src={avatar} alt={p.user.fullName} />}
                <AvatarFallback>{initials(p.user.fullName)}</AvatarFallback>
              </Avatar>
              {onboardingPending && (
                <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-destructive ring-2 ring-background" aria-label={t('mhdr.onboardingDot')} />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[17px] font-semibold leading-tight text-foreground">{p.user.fullName}</span>
              {p.user.orgName && <span className="mt-0.5 block truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">{p.user.orgName}</span>}
            </span>
          </button>
          <button type="button" aria-label={t('common:actions.search')} onClick={() => setSearchOpen((v) => !v)} className="flex h-10 w-10 items-center justify-center rounded-full text-foreground active:bg-muted">
            <Search className="h-6 w-6" strokeWidth={1.8} />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" aria-label={t('mhdr.createMenu')} className="flex h-10 w-10 items-center justify-center rounded-full text-foreground active:bg-muted">
                <Plus className="h-7 w-7" strokeWidth={1.6} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuItem onClick={p.onNewConversation}><MessageSquarePlus className="mr-2 h-4 w-4" />{t('mhdr.newConv')}</DropdownMenuItem>
              <DropdownMenuItem onClick={p.onAddFriend}><UserPlus className="mr-2 h-4 w-4" />{t('mhdr.addFriend')}</DropdownMenuItem>
              <DropdownMenuItem onClick={p.onMarkAllRead}><CheckCheck className="mr-2 h-4 w-4" />{t('mhdr.markAllRead')}</DropdownMenuItem>
              <DropdownMenuItem onClick={p.onRefresh}><RefreshCw className="mr-2 h-4 w-4" />{t('common:actions.refresh')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </Collapsible>

      {searchOpen && (
        <div className="relative mt-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            autoFocus
            value={p.search}
            onChange={(e) => p.onSearchChange(e.target.value)}
            placeholder={t('mhdr.searchPlaceholder')}
            className="h-10 w-full rounded-full bg-secondary pl-9 pr-9 text-base outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/30"
          />
          <button type="button" aria-label={t('mhdr.closeSearch')} onClick={() => { p.onSearchChange(''); setSearchOpen(false) }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ② Hàng avatar tròn = tài khoản kênh: 6 ô CỐ ĐỊNH chia đều (không cuộn ngang) — "Tất cả" + tối đa 4 kênh đầu theo thứ tự người dùng xếp +
          nút mở rộng ở ô cuối (từ 2 kênh). Đệm pt-2 / pb-1: chừa chỗ cho vòng chọn (ring + offset ≈ 4px) — khung `Collapsible` cắt phần tràn ra ngoài. */}
      {p.accounts.length > 0 && (
        <Collapsible collapsed={collapsed}>
          <ul className="mt-2 grid grid-cols-6 gap-x-1 pb-1 pt-2" aria-label={t('chSheet.title')}>
            <AccountItem label={t('common:misc.all')} active={p.accountId === undefined} onClick={() => p.onAccountChange(undefined)}>
              <img src="/assets/logo-mark.png" alt="" className="h-6 w-6 object-contain" draggable={false} />
            </AccountItem>
            {shownAccounts.map((a) => {
              const name = a.displayName || platformLabelOf(a.platform)
              const src = avatarSrc(a.avatarUrl)
              return (
                <AccountItem key={a.id} label={name} active={p.accountId === a.id} badge={unreadOf(a.id)} onClick={() => p.onAccountChange(p.accountId === a.id ? undefined : a.id)}>
                  <Avatar className="h-full w-full">
                    {src && <AvatarImage src={src} alt={name} />}
                    <AvatarFallback className="text-xs">{initials(name)}</AvatarFallback>
                  </Avatar>
                  <PlatformBadge platform={a.platform} />
                </AccountItem>
              )
            })}
            {/* Nút mở rộng (ô cuối): danh sách đầy đủ để chọn kênh; muốn đổi thứ tự thì bấm "Sắp xếp" trong hộp. */}
            {hasExpand(p.accounts.length) && (
              <AccountItem label={t('mhdr.expand')} active={false} badge={hiddenUnread} onClick={() => setAccountsOpen(true)} ariaLabel={hiddenUnread > 0 ? t('mhdr.expandAriaUnread', { count: hiddenUnread }) : t('mhdr.expandAria')}>
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              </AccountItem>
            )}
          </ul>
        </Collapsible>
      )}
      <ChannelsSheet
        open={accountsOpen}
        onOpenChange={setAccountsOpen}
        accounts={orderedAccounts}
        accountId={p.accountId}
        unreadOf={unreadOf}
        onPick={(id) => { p.onAccountChange(id); setAccountsOpen(false) }}
        onReorder={setChannelOrder}
        onReset={() => setChannelOrder([])}
        isDefault={channelsDefault}
      />

      {/* ③ Nút lọc + thanh phân đoạn */}
      <div className="mt-2.5 flex items-center gap-2 pb-2">
        <button type="button" aria-label={t('mhdr.filterAria')} onClick={() => setFilterOpen(true)} className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-foreground active:bg-secondary/70">
          <SlidersHorizontal className="h-5 w-5" strokeWidth={1.8} />
          {hiddenFilter && <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-primary ring-2 ring-secondary" aria-label={t('mhdr.filtering')} />}
        </button>
        <div className="flex min-w-0 flex-1 rounded-full bg-secondary p-0.5">
          {/* Phân đoạn: vừa thì chia đều, nhiều quá thì cuộn ngang (không cắt chữ). */}
          <div role="tablist" aria-label={t('mhdr.quickFilters')} className="flex min-w-0 flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {segments.map((id) => {
              const active = segmentActive(id)
              const n = segmentCountOf(id, p.counts)
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => selectSegment(id)}
                  className={cn(
                    'flex h-9 min-w-fit flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-full px-2 text-[14px] transition-colors',
                    active ? 'bg-background font-medium text-foreground shadow-sm' : 'text-muted-foreground active:text-foreground',
                  )}
                >
                  {tagOfSegment(id) !== undefined && <TagIcon className="h-3.5 w-3.5 shrink-0" />}
                  <span className="max-w-[9rem] truncate">{segmentLabel(id)}</span>
                  {n > 0 && <span className={cn('shrink-0 text-[13px] tabular-nums', active ? 'text-foreground' : 'text-muted-foreground')}>{shortCount(n)}</span>}
                </button>
              )
            })}
          </div>
          <button type="button" onClick={() => setEditOpen(true)} aria-label={t('segSheet.title')} className="flex h-9 shrink-0 items-center justify-center rounded-full px-1.5 text-[13px] font-medium text-primary active:bg-background/60">
            {t('common:actions.edit')}
          </button>
        </div>
      </div>

      {hiddenLabel && (
        <div className="flex items-center gap-2 pb-2">
          <span className="text-xs text-muted-foreground">{t('mhdr.filteringNow')}</span>
          <button
            type="button"
            onClick={() => { p.onFilterChange('all'); p.onTagChange(undefined) }}
            aria-label={t('mhdr.clearFilterAria', { label: hiddenLabel })}
            className="inline-flex h-7 max-w-full items-center gap-1 rounded-full bg-accent pl-2.5 pr-1.5 text-xs font-medium text-primary active:opacity-80"
          >
            <span className="truncate">{hiddenLabel}</span>
            <X className="h-3.5 w-3.5 shrink-0" />
          </button>
        </div>
      )}

      <SegmentsSheet open={editOpen} onOpenChange={setEditOpen} segments={segments} onChange={changeSegments} tags={tagDefs ?? []} />
      <MobileAccountDrawer open={accountOpen} onOpenChange={setAccountOpen} />

      <SideDrawer open={filterOpen} onOpenChange={setFilterOpen}>
        <SideDrawerContent title={t('mhdr.drawerTitle')}>
          {p.renderFilterRail({ onNavigate: () => setFilterOpen(false), topSlot: <DrawerTopSlot {...p} /> })}
        </SideDrawerContent>
      </SideDrawer>
    </div>
  )
}

/**
 * Thu/mở theo chiều cao THẬT của nội dung mà không cần biết trước chiều cao (kỹ thuật `grid-template-rows: 0fr → 1fr`)
 * — tránh phải đoán `max-h-[…]` rồi bị cắt chữ khi cỡ chữ hệ thống lớn hơn bình thường.
 */
function Collapsible({ collapsed, children }: { collapsed: boolean; children: React.ReactNode }) {
  return (
    <div className={cn('grid transition-[grid-template-rows,opacity] duration-200 ease-in-out', collapsed ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100')}>
      <div className="overflow-hidden">{children}</div>
    </div>
  )
}

function AccountItem({ label, active, onClick, children, ariaLabel, badge = 0 }: { label: string; active: boolean; onClick: () => void; children: React.ReactNode; ariaLabel?: string; /** Số hội thoại chưa đọc ở tài khoản này (khi đang xem tài khoản khác); 0 = không hiện. */ badge?: number }) {
  const { t } = useTranslation('convList')
  return (
    <li className="min-w-0">
      <button type="button" onClick={onClick} aria-pressed={ariaLabel ? undefined : active} aria-label={ariaLabel} className="flex w-full flex-col items-center gap-1 active:opacity-70">
        <span className={cn('relative flex h-11 w-11 items-center justify-center rounded-full bg-secondary', active && 'ring-2 ring-primary ring-offset-2 ring-offset-background')}>
          {children}
          {badge > 0 && (
            <span aria-label={t('shared.unreadCount', { count: badge })} className="absolute -right-1 -top-1 z-10 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold leading-none text-destructive-foreground ring-2 ring-background">
              {shortUnreadCount(badge)}
            </span>
          )}
        </span>
        <span className={cn('w-full truncate text-center text-[11px] leading-tight', active ? 'font-medium text-foreground' : 'text-muted-foreground')}>{label}</span>
      </button>
    </li>
  )
}

/** Đầu ngăn kéo: phân loại Ưu tiên / Nội bộ (ở máy tính nằm dưới ô tìm kiếm) + nhóm kênh. */
function DrawerTopSlot(p: MobileInboxHeaderProps) {
  const { t } = useTranslation('convList')
  return (
    <div className="space-y-3 px-3 pb-3">
      <div className="flex rounded-full bg-secondary p-1">
        {([['main', t('mhdr.tabMain')], ['internal', t('mhdr.tabInternal')]] as const).map(([v, label]) => (
          <button key={v} type="button" onClick={() => p.onTabChange(v)} className={cn('flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full text-[15px]', p.tab === v ? 'bg-background font-medium text-foreground shadow-sm' : 'text-muted-foreground')}>
            {label}
            {p.tabUnread[v] > 0 && <span className="rounded-full bg-destructive px-1.5 text-[11px] font-semibold leading-[18px] text-destructive-foreground">{shortCount(p.tabUnread[v])}</span>}
          </button>
        ))}
      </div>
      {p.channelGroups.length > 1 && (
        <ChannelChips groups={p.channelGroups} value={p.channel} onChange={p.onChannelChange} className="flex-wrap gap-2 overflow-visible [&_button]:px-3 [&_button]:py-1.5 [&_button]:text-xs" />
      )}
    </div>
  )
}
